import React from 'react';
import './IntertownBooking.css';
import { formatRupees, placeLabel, routeFare } from '../utils/intertownDisplay';
import {
  IconAlert, IconArrowRight, IconCheck, IconChevronRight, IconInfo, IconPin,
  IconSearch, IconShield, IconSwap, IconTarget,
} from './IntertownIcons';

const RECOVERY_STEPS = new Set(['change_pickup', 'change_destination', 'retry']);
const NO_ARROW_STEPS = new Set(['calculating', 'submitting', 'finish_ride']);

/**
 * The Intertown booking screen: trip type, journey card (pickup, destination,
 * fare), route choices, and the sticky request bar. Purely presentational: the
 * page owns every piece of state and passes it in, so each state can be drawn
 * without a login or a network.
 */
export default function IntertownBookingView({
  t, config, routes, selectedRoute, isCustom, customEnabled, tripType, onTripType,
  pickup, destination, pickupNote, onPickupNote, quote, quoteError, loadingQuote,
  step, stepLabel, actionable, onAction, pickupRadiusKm, onEditPickup, onChooseRoute, onChooseCustom,
  exactDrop = null, townReady = false, onPickExactSpot, onClearExactSpot,
}) {
  const perKm = config?.perKm;
  const isReturn = tripType === 'return';
  const tripLabel = isReturn ? t('intertown.returnTrip') : t('intertown.oneWay');
  const barTripLabel = isReturn ? t('intertown.returnShort') : t('intertown.oneWay');
  const perKmText = t('intertown.perKm', { rate: formatRupees(perKm) });

  const oneWay = isCustom ? null : routeFare(selectedRoute, 'one_way', perKm);
  const ret = isCustom ? null : routeFare(selectedRoute, 'return', perKm);
  const oneWaySub = oneWay?.fare != null ? formatRupees(oneWay.fare) : perKmText;
  const returnSub = ret?.fare != null ? t('intertown.fromFare', { fare: formatRupees(ret.fare) }) : perKmText;

  const listed = isCustom ? null : routeFare(selectedRoute, tripType, perKm);
  const fare = quote?.baseFare ?? listed?.fare ?? null;
  const isFixed = quote ? quote.pricingMode === 'fixed' : listed?.mode === 'fixed';
  const customRoute = isCustom && quote?.isCustom ? quote.route : null;

  const distanceKm = quote?.route?.distanceKm || selectedRoute?.distanceKm || 0;
  const routeName = isCustom ? placeLabel(destination) : selectedRoute?.toName || '';
  const barDetail = isCustom && !destination
    ? t('intertown.fareByDistance')
    : [routeName, distanceKm ? `${distanceKm} km` : null].filter(Boolean).join(' · ');

  const noDestinationYet = isCustom ? !destination : !selectedRoute;
  const canPickDestination = customEnabled && noDestinationYet;

  let farePanel = null;
  if (quoteError) {
    farePanel = (
      <div className="it2-alert" role="alert">
        <span className="it2-alert-ico"><IconAlert size={20} /></span>
        <div className="it2-alert-text"><b>{quoteError.message}</b></div>
        {RECOVERY_STEPS.has(step) && (
          <button type="button" className="it2-alert-btn" onClick={onAction}>{stepLabel}</button>
        )}
      </div>
    );
  } else if (isCustom && loadingQuote) {
    farePanel = (
      <div className="it2-fare is-quiet"><div><b>{t('intertown.calculatingFare')}</b></div></div>
    );
  } else if (fare != null) {
    farePanel = (
      <div className="it2-fare">
        <div>
          <b>{isFixed ? t('intertown.fixedFareRoute') : (isCustom ? t('intertown.yourFare') : t('intertown.fareForRoute'))}</b>
          <small>{customRoute ? `${customRoute.distanceKm} km · ` : ''}{tripLabel}</small>
        </div>
        <strong>{formatRupees(fare)}</strong>
      </div>
    );
  }

  const destinationRow = (() => {
    if (!isCustom && selectedRoute) {
      // A popular route is the town; the rider may also name their own spot inside it (the fare
      // stays the route's flat fare). The spot option needs custom map destinations to be on.
      const spotLabel = exactDrop ? placeLabel(exactDrop) : '';
      return (
        <>
          <div className="it2-row it2-row-static">
            <span className="it2-pin"><IconPin size={22} /></span>
            <div className="it2-row-main">
              <small>{t('intertown.destinationShort')}</small>
              {exactDrop ? (
                <>
                  <b>{spotLabel}</b>
                  {exactDrop.address && exactDrop.address !== spotLabel && <span>{exactDrop.address}</span>}
                  <span>{t('intertown.exactSpotNote', { town: selectedRoute.toName })}</span>
                </>
              ) : (
                <>
                  <b>{selectedRoute.toName}</b>
                  <span>{selectedRoute.distanceKm} km · ~{selectedRoute.durationMin} min</span>
                </>
              )}
            </div>
          </div>
          {customEnabled && onPickExactSpot && (
            <div className="it2-spot-row">
              <button type="button" className="it2-spot" onClick={onPickExactSpot} disabled={!townReady}>
                <IconTarget size={16} />
                {exactDrop ? t('intertown.exactSpotChange') : t('intertown.exactSpotCta', { town: selectedRoute.toName })}
              </button>
              {exactDrop && (
                <button type="button" className="it2-spot is-quiet" onClick={onClearExactSpot}>{t('intertown.useTownCentre')}</button>
              )}
            </div>
          )}
        </>
      );
    }
    const chosen = isCustom && destination;
    return (
      <button type="button" className="it2-row" onClick={canPickDestination || chosen ? onChooseCustom : undefined} disabled={!customEnabled}>
        <span className="it2-pin"><IconPin size={22} /></span>
        <div className="it2-row-main">
          <small>{t('intertown.destinationShort')}</small>
          {chosen ? (
            <>
              <b>{placeLabel(destination)}</b>
              {destination.address && destination.address !== placeLabel(destination) && <span>{destination.address}</span>}
              {customRoute && <span>{customRoute.distanceKm} km · ~{customRoute.durationMin} min</span>}
            </>
          ) : (
            <>
              <b className="is-placeholder">{t('intertown.chooseDestinationFare')}</b>
              <span>{t('intertown.customLimits', { min: config?.custom?.minDistanceKm, max: config?.custom?.maxDistanceKm })}</span>
            </>
          )}
        </div>
        {customEnabled && (
          chosen
            ? <span className="it2-link">{t('intertown.change')}</span>
            : <span className="it2-pill"><IconTarget size={16} />{t('intertown.setOnMap')}</span>
        )}
      </button>
    );
  })();

  return (
    <>
      <section className="it2-types" aria-label={t('intertown.tripTypeLabel')}>
        <button type="button" className={`it2-type${!isReturn ? ' on' : ''}`} aria-pressed={!isReturn} onClick={() => onTripType('one_way')} disabled={!config?.oneWayEnabled}>
          <span className="it2-type-ico"><IconArrowRight size={22} /></span>
          <span><b>{t('intertown.oneWay')}</b><small>{oneWaySub}</small></span>
        </button>
        <button type="button" className={`it2-type${isReturn ? ' on' : ''}`} aria-pressed={isReturn} onClick={() => onTripType('return')} disabled={!config?.returnEnabled}>
          <span className="it2-type-ico"><IconSwap size={22} /></span>
          <span><b>{t('intertown.returnTrip')}</b><small>{returnSub}</small></span>
        </button>
      </section>

      {!pickup && pickupRadiusKm != null && (
        <div className="it2-note"><IconInfo size={18} /><span>{t('intertown.pickupRadiusNote', { max: pickupRadiusKm })}</span></div>
      )}

      <section className="it2-journey">
        <button type="button" className="it2-row it2-row-pickup" onClick={onEditPickup}>
          <span className="it2-dot" aria-hidden="true" />
          <div className="it2-row-main">
            <small>{t('intertown.pickupShort')}</small>
            {pickup ? (
              <>
                <b>{placeLabel(pickup)}</b>
                {pickup.address && pickup.address !== placeLabel(pickup) && <span>{pickup.address}</span>}
              </>
            ) : (
              <>
                <b className="is-placeholder">{t('intertown.choosePickup')}</b>
                <span>{t('intertown.pickupMapHint')}</span>
              </>
            )}
          </div>
          {pickup
            ? <span className="it2-link">{t('intertown.editPickup')}</span>
            : <span className="it2-pill"><IconTarget size={16} />{t('intertown.setOnMap')}</span>}
        </button>
        <div className="it2-divider" />
        {destinationRow}
        {farePanel}
        {pickup && (
          <textarea
            id="intertown-pickup"
            className="it2-note-input"
            value={pickupNote}
            onChange={(event) => onPickupNote(event.target.value)}
            maxLength={180}
            placeholder={t('intertown.pickupNotePlaceholder')}
            rows="2"
          />
        )}
      </section>

      {isReturn && quote?.waiting && (
        <section className="intertown-waiting-card">
          <span>⏳</span>
          <div>
            <b>{t('intertown.waitingTitle')}</b>
            <p>{t('intertown.waitingBody', {
              free: quote.waiting.freeMinutes,
              rate: formatRupees(quote.waiting.rate),
              unit: t(quote.waiting.unit === 'hour' ? 'intertown.unitHour' : 'intertown.unitMinute'),
              max: quote.waiting.maxMinutes,
            })}</p>
          </div>
        </section>
      )}

      {routes.length > 1 && (
        <section className="it2-section">
          <h3 className="it2-h">{t('intertown.popularRoutes')}</h3>
          <div role="radiogroup" aria-label={t('intertown.popularRoutes')} className="it2-routes">
            {routes.map((route) => {
              const on = !isCustom && selectedRoute?.id === route.id;
              const priced = routeFare(route, tripType, perKm);
              const price = priced.fare == null ? null
                : (isReturn ? t('intertown.fromFare', { fare: formatRupees(priced.fare) }) : formatRupees(priced.fare));
              return (
                <button type="button" role="radio" aria-checked={on} key={route.id} className={`it2-route${on ? ' on' : ''}`} onClick={() => onChooseRoute(route.id)}>
                  <span className="it2-radio" aria-hidden="true" />
                  <span className="it2-route-main">
                    <b>{route.toName}</b>
                    <small>{route.distanceKm} km · ~{route.durationMin} min{price ? ` · ${price}` : ''}</small>
                  </span>
                  {on && <span className="it2-badge"><IconCheck size={14} />{t('intertown.selected')}</span>}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {customEnabled && (
        <button type="button" className={`it2-card it2-else${isCustom ? ' on' : ''}`} onClick={onChooseCustom}>
          <span className="it2-card-ico"><IconSearch size={22} /></span>
          <span className="it2-card-main">
            <b>{t('intertown.somewhereElse')}</b>
            <span>{t('intertown.somewhereElseBody')}</span>
            <small>{t('intertown.fareFromDistance')}</small>
          </span>
          <span className="it2-chev"><IconChevronRight size={20} /></span>
        </button>
      )}

      <div className="it2-card it2-info">
        <span className="it2-card-ico"><IconShield size={22} /></span>
        <span className="it2-card-main"><span>{t('intertown.privacyNote')}</span></span>
      </div>

      <footer className="it2-bar">
        <div className="it2-bar-fare">
          <strong>
            {fare != null
              ? <>{formatRupees(fare)}<span> · {barTripLabel}</span></>
              : perKmText}
          </strong>
          <small>{barDetail}</small>
        </div>
        <button type="button" className="it2-cta" onClick={onAction} disabled={!actionable}>
          {stepLabel}
          {!NO_ARROW_STEPS.has(step) && <IconArrowRight size={18} />}
        </button>
      </footer>
    </>
  );
}

